import React, { useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames";
import DropdownPortal from "src/app/components/DropdownPortal";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import useGetCurrencies from "src/hooks/useGetCurrencies";
import { ReactComponent as ChevronDownIcon } from "src/images/chevron-down.svg";

import "./CurrencyPickerDropdown.scss";

interface Props {
  currentCurrencyCode: string;
  columnCurrencyCode: string;
  onSelect: (newCurrencyCode: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
  returnFocusRef?: React.RefObject<HTMLInputElement>;
  // Shows the chevron even while the picker is closed - used by CurrencyInput
  // so the trigger only reads as interactive once the sibling value input is
  // actually focused (editing), matching the tasks table's click-to-edit cells.
  forceVisible?: boolean;
}

const PINNED_CODES = ["USD", "EUR", "GBP", "PLN", "AED"];

export function CurrencyPickerDropdown(props: Props) {
  const {
    currentCurrencyCode,
    columnCurrencyCode,
    onSelect,
    onOpenChange,
    returnFocusRef,
    forceVisible,
  } = props;

  const displayCode = currentCurrencyCode || columnCurrencyCode;

  const { currencies } = useGetCurrencies();

  const [visible, setVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const searchInputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    onOpenChange?.(true);
    setSearchTerm("");
    setVisible(true);
  };

  const closePicker = (focusReturn: boolean) => {
    setVisible(false);
    onOpenChange?.(false);
    if (focusReturn) {
      returnFocusRef?.current?.focus();
    }
  };

  useEffect(() => {
    if (visible) {
      searchInputRef.current?.focus();
    }
  }, [visible]);

  const sortedOptions = useMemo(() => {
    const rest = currencies
      .filter((currency) => currency.code !== columnCurrencyCode)
      .sort((a, b) => {
        const aPinned = PINNED_CODES.includes(a.code);
        const bPinned = PINNED_CODES.includes(b.code);

        if (aPinned && bPinned) {
          return PINNED_CODES.indexOf(a.code) - PINNED_CODES.indexOf(b.code);
        }
        if (aPinned) return -1;
        if (bPinned) return 1;
        return a.code.localeCompare(b.code);
      });

    const column = currencies.find(
      (currency) => currency.code === columnCurrencyCode,
    );

    return column ? [column, ...rest] : rest;
  }, [currencies, columnCurrencyCode]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return sortedOptions;

    const term = searchTerm.toUpperCase();

    return sortedOptions.filter(
      (currency) =>
        currency.code.toUpperCase().includes(term) ||
        currency.name.toUpperCase().includes(term),
    );
  }, [sortedOptions, searchTerm]);

  const selectCurrency = (code: string) => {
    onSelect(code);
    closePicker(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closePicker(true);
    }
  };

  const overlay = (
    <div className="currency-picker-dropdown">
      <div className="currency-picker-dropdown__search">
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <ChevronDownIcon className="currency-picker-dropdown__search-chevron" />
      </div>

      <div className="currency-picker-dropdown__options">
        {filteredOptions.map((currency) => (
          <div
            key={currency.code}
            className="currency-picker-dropdown__option"
            onClick={() => selectCurrency(currency.code)}
          >
            <span className="currency-picker-dropdown__option-code">
              {currency.code}
            </span>
            <span className="currency-picker-dropdown__option-name">
              {currency.name}
            </span>
            {currency.code === columnCurrencyCode && (
              <span className="currency-picker-dropdown__pin-badge">
                <IDHFormattedMessage id="ws_default" defaultMessage="Default" />
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <DropdownPortal
      className="currency-picker-dropdown-portal"
      overlay={overlay}
      visible={visible}
      onVisibleChange={(next: boolean) => {
        if (next) {
          // see the button's onClick: not editing yet -> focus only
          if (!visible && forceVisible === false) {
            returnFocusRef?.current?.focus();
            return;
          }
          setVisible(true);
          return;
        }
        closePicker(true);
      }}
    >
      <button
        type="button"
        className={classNames("currency-code", "currency-code--interactive", {
          "currency-code--open": visible || forceVisible,
        })}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          // not editing yet: the first click only puts the cursor in the
          // value input, the picker needs a second click on the code
          if (!visible && forceVisible === false) {
            returnFocusRef?.current?.focus();
            return;
          }
          openPicker();
        }}
      >
        {displayCode}
        <ChevronDownIcon className="currency-code__chevron" />
      </button>
    </DropdownPortal>
  );
}
